<template>
  <div class="flex flex-col h-screen ext-shell text-gray-900">
    <ClientTopBar
      :active-id="activeTab ? activeTab.key : null"
      :nav-items="navItems"
      @change="setTab"
    />
    <div class="flex-1 flex min-h-0 overflow-hidden">
      <ClientSideNav
        :title="activeTab ? activeTab.label : ''"
        :items="sideNavItems"
        :active-domain-id="activePageId"
        :show-export="!!page && isSpec"
        :exporting="exporting"
        @select="setPage($event.id)"
        @export="onExport"
      />
      <main class="flex-1 overflow-auto ext-page">
        <div :key="`${entityKey}-${activePageId}`" class="py-6 px-16">
          <LoadingIndicator v-if="loading" message="Loading..." />
          <p v-else-if="error" class="text-sm text-red-700">{{ error }}</p>
          <p v-else-if="!tabs.length" class="text-sm text-gray-500">No pages have been published to this portal yet.</p>
          <template v-else-if="page">
            <PageCanvas
              v-if="isSpec"
              ref="canvas"
              :page-id="page.id"
              :spec="page.spec"
              :widgets="page.widgets || {}"
              :host="host"
              hide-entity-filter
              @drill-entity="drill"
              @select-patient="openPatient"
            />
            <AgentHtmlCard v-else :html="page.html" :sources="page.sources || []" :page-id="page.id" />
          </template>
        </div>
      </main>
    </div>
    <ClientFooter />
  </div>
</template>

<script>
import axios from 'axios';
import './table/extTable.css';
import ClientTopBar from './ClientTopBar.vue';
import ClientSideNav from './ClientSideNav.vue';
import ClientFooter from './ClientFooter.vue';
import AgentHtmlCard from '@/components/Beta/AgentHtmlCard.vue';
import PageCanvas from '@/components/Beta/widgets/PageCanvas.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { canAccessEntity } from '@/utils/access';
import { exportBlockedInDemoMode } from '@/utils/demoEgress';
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import { fileSlug } from '@/utils/workflowExports';
import { fetchPortalTabs, portalNavItems } from '@/utils/externalPortal';

export default {
  name: 'ExternalModule',
  components: { ClientTopBar, ClientSideNav, ClientFooter, AgentHtmlCard, PageCanvas, LoadingIndicator },
  props: {
    entityid: { type: [String, Number], default: null },
  },
  data() {
    return {
      tabs: [],
      activePageId: null,
      page: null,
      loading: true,
      error: '',
      exporting: false,
      entityKey: 0,
    };
  },
  computed: {
    entityIdNumber() {
      return this.entityid ? Number(this.entityid) : null;
    },
    host() {
      return { entity: this.entityIdNumber };
    },
    navItems() {
      return portalNavItems(this.tabs);
    },
    activeTab() {
      return this.tabs.find((tab) => tab.pages.some((p) => p.id === this.activePageId)) || null;
    },
    sideNavItems() {
      if (!this.activeTab || this.activeTab.pages.length < 2) return [];
      return this.activeTab.pages.map((p) => ({ id: p.id, label: p.title }));
    },
    isSpec() {
      return !!(this.page && this.page.spec && (this.page.spec.placements || []).length);
    },
  },
  watch: {
    entityid() {
      this.entityKey++;
    },
    '$route.query.page'(value) {
      const id = Number(value);
      if (id && id !== this.activePageId && this.tabs.some((tab) => tab.pages.some((p) => p.id === id))) this.setPage(id, true);
    },
  },
  async mounted() {
    try {
      this.tabs = await fetchPortalTabs();
    } catch {
      this.error = 'The portal could not be loaded.';
      this.loading = false;
      return;
    }
    const first = this.initialPage();
    if (first) await this.setPage(first, true);
    else this.loading = false;
  },
  methods: {
    initialPage() {
      const asked = Number(this.$route.query.page);
      if (asked && this.tabs.some((tab) => tab.pages.some((p) => p.id === asked))) return asked;
      const tab = this.tabs.find((t) => t.key === this.$route.query.tab) || this.tabs[0];
      return tab && tab.pages.length ? tab.pages[0].id : null;
    },
    setTab(key) {
      const tab = this.tabs.find((t) => t.key === key);
      if (tab && tab.pages.length) this.setPage(tab.pages[0].id);
    },
    async setPage(id, skipSync) {
      this.activePageId = id;
      if (!skipSync) this.syncQuery();
      await this.loadPage(id);
    },
    syncQuery() {
      const query = { ...this.$route.query, page: String(this.activePageId) };
      delete query.tab;
      delete query.view;
      if (query.page !== this.$route.query.page) this.$router.replace({ query }).catch(() => {});
    },
    async loadPage(id) {
      this.loading = true;
      this.error = '';
      this.page = null;
      try {
        const { data } = await axios.get(`/api/impact_analytics/agent-pages/${id}/`);
        if (this.activePageId === id) this.page = data;
      } catch (err) {
        this.error = err?.response?.data?.error || 'That page could not be loaded.';
      } finally {
        if (this.activePageId === id) this.loading = false;
      }
    },
    drill(row) {
      const id = Number(row && row.entity_id);
      if (!id || id === this.entityIdNumber || !canAccessEntity(id)) return;
      this.$router.push({ name: 'ExternalEntity', params: { entityid: id }, query: this.$route.query });
    },
    openPatient({ bene_mbi_id }) {
      if (bene_mbi_id) this.$router.push({ name: 'ExternalPatientDetail', params: { patientIdentifier: String(bene_mbi_id) } });
    },
    async onExport() {
      if (exportBlockedInDemoMode() || !this.$refs.canvas) return;
      this.exporting = true;
      try {
        const sheets = await this.$refs.canvas.exportRows();
        if (!sheets.length) return;
        await exportToExcel(sheets.map((x) => x.rows), `${fileSlug(this.page.title)}.xlsx`, {
          sheetNames: sheets.map((x, i) => (x.name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 28) || `Sheet ${i + 1}`)),
        });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style>
/* ── External editorial theme ── */
.ext-shell {
  background: #F9F6F1;
}
.ext-page {
  background: #F9F6F1;
}

/* Section header — serif editorial style */
.ext-section-number {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: #7D7770;
  margin-bottom: 4px;
}
.ext-section-title {
  font-family: 'IBM Plex Serif', Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: #0F0D0B;
  line-height: 1.15;
  letter-spacing: -0.015em;
  margin-bottom: 6px;
}
.ext-section-desc {
  font-size: 13px;
  color: #7D7770;
  line-height: 1.5;
  max-width: 780px;
}
.ext-section-meta {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-size: 11px;
  color: #7D7770;
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
}

/* Hero stat block */
.ext-hero {
  background: #FFFFFF;
  border: 1px solid #F2EEE8;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 24px;
}
.ext-hero-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8E8F90;
  margin-bottom: 6px;
}
.ext-hero-value {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-feature-settings: "tnum", "zero";
  font-size: 34px;
  font-weight: 600;
  color: #0F0D0B;
  line-height: 1;
  letter-spacing: -0.02em;
}
.ext-hero-unit {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #A9A29A;
  margin-left: 6px;
  margin-left: 6px;
}
.ext-hero-sub {
  font-size: 12px;
  color: #A5A5A6;
  margin-top: 8px;
}

/* Change tag */
.ext-change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
}
.ext-change.positive { color: #389A6C; }
.ext-change.negative { color: #D53D3D; }

/* ── Shared component overrides for external editorial theme ── */

/* Shared components — white card background on table wrappers */
.ext-page .border.border-gray-200.rounded-lg {
  background: #FFFFFF;
  border-color: #F2EEE8 !important;
}

/* Force editorial font stack on entire external page */
.ext-page,
.ext-page * {
  font-family: 'IBM Plex Sans', system-ui, -apple-system, sans-serif;
  font-size: 12px;
}
.ext-page .font-mono,
.ext-page [class*="mono"],
.ext-page code,
.ext-page .tabular-nums {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace !important;
}
</style>
